import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChevronDown,
  ChevronRight,
  Info,
  Map,
  Users,
  Save,
  Trash2,
  X,
  AlertCircle,
  Mail
} from 'lucide-react';
import GoogleMap from '../components/GoogleMap';
import FestivalForm from '../components/FestivalForm';
import UserLoginStatusInfolet from '../components/UserLoginStatusInfolet';
import CollaboratorsList from '../components/CollaboratorsList';


// POI Icons (temporary)
const POI_ICONS = {
  stage: '🎭',
  food_stall: '🍔',
  restroom: '🚻',
  information: 'ℹ️',
  emergency: '🚑',
  ticket_booth: '🎫',
  entry_point: '🚪',
  exit_point: '🚪',
  parking: '🅿️',
  transportation: '🚌',
  beer_tent: '🍺',
  other: '📍'
};

const POI_TYPES = [
  { id: 'stage', name: 'Stage', icon: POI_ICONS.stage },
  { id: 'food_stall', name: 'Food Stall', icon: POI_ICONS.food_stall },
  { id: 'restroom', name: 'Restroom', icon: POI_ICONS.restroom },
  { id: 'information', name: 'Information Booth', icon: POI_ICONS.information },
  { id: 'emergency', name: 'Emergency Services', icon: POI_ICONS.emergency },
  { id: 'ticket_booth', name: 'Ticket Booth', icon: POI_ICONS.ticket_booth },
  { id: 'entry_point', name: 'Entry Point', icon: POI_ICONS.entry_point },
  { id: 'exit_point', name: 'Exit Point', icon: POI_ICONS.exit_point },
  { id: 'parking', name: 'Parking', icon: POI_ICONS.parking },
  { id: 'transportation', name: 'Transportation', icon: POI_ICONS.transportation },
  { id: 'beer_tent', name: 'Beer Tent', icon: POI_ICONS.beer_tent },
  { id: 'other', name: 'Other', icon: POI_ICONS.other }
];

const FestivalEditor = () => {
  const { festivalId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeView, setActiveView] = useState('map_overview');
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [activeTab, setActiveTab] = useState('quick_create');
  const [unplacedPOIs, setUnplacedPOIs] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({
    basic_info: false,
    map_overview: true,
    collaborators: false
  });
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  // Fetch festival data
  const { data: festivalData, isLoading } = useQuery(
    ['festival', festivalId],
    async () => {
      const response = await axios.get(`/api/festivals/${festivalId}`);
      return response.data;
    }
  );

  // Fetch POIs
  const { data: poisData } = useQuery(
    ['pois', festivalId],
    async () => {
      const response = await axios.get(`/api/festivals/${festivalId}/pois`);
      return response.data;
    },
    {
      enabled: !!festivalId
    }
  );

  // Create POI mutation
  const createPOIMutation = useMutation(
    async (poiData) => {
      const response = await axios.post(`/api/festivals/${festivalId}/pois`, poiData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pois', festivalId]);
        toast.success('POI created successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create POI');
      }
    }
  );

  // Update POI mutation
  const updatePOIMutation = useMutation(
    async ({ id, ...poiData }) => {
      const response = await axios.put(`/api/festivals/${festivalId}/pois/${id}`, poiData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pois', festivalId]);
        toast.success('POI updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update POI');
      }
    }
  );

  // Delete POI mutation
  const deletePOIMutation = useMutation(
    async (poiId) => {
      await axios.delete(`/api/festivals/${festivalId}/pois/${poiId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pois', festivalId]);
        setSelectedPOI(null);
        toast.success('POI deleted successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete POI');
      }
    }
  );

  // Update festival mutation
  const updateFestivalMutation = useMutation(
    async (updatedData) => {
      // Server will add creator_id from session
      const response = await axios.put(`/api/festivals/${festivalId}`, updatedData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['festival', festivalId]);
        toast.success('Festival updated successfully!');
      },
      onError: (error) => {
        // Check if this is a verification required error
        if (error.response?.data?.requiresVerification) {
          setShowVerificationDialog(true);
        } else {
          toast.error(error.response?.data?.error || 'Failed to update festival');
        }
      }
    }
  );

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
    if (nodeId !== 'map_overview') {
      setActiveView(nodeId);
    }
  };

  const handlePOIDrop = useCallback((lat, lng, poiTypeOrData, poiId = null) => {
    // Check if this is an unplaced POI being dropped
    if (typeof poiTypeOrData === 'object' && poiTypeOrData.type === 'unplaced_poi') {
      const unplacedPOI = poiTypeOrData.poi;

      // Create the POI in the database with the dropped location
      const newPOI = {
        name: unplacedPOI.name,
        category: unplacedPOI.category,
        latitude: lat,
        longitude: lng,
        description: unplacedPOI.description,
        area_polygon: null,
        start_date: null,
        end_date: null,
        metadata: unplacedPOI.category === 'stage' ? { artists: [] } : null
      };

      createPOIMutation.mutate(newPOI);

      // Remove from unplaced POIs
      setUnplacedPOIs(prev => prev.filter(poi => poi.id !== unplacedPOI.id));

    } else if (poiId) {
      // Update existing POI position
      updatePOIMutation.mutate({
        id: poiId,
        latitude: lat,
        longitude: lng
      });
    } else {
      // Create new POI from palette
      const poiType = poiTypeOrData;
      const newPOI = {
        name: `New ${POI_TYPES.find(t => t.id === poiType)?.name || 'POI'}`,
        category: poiType,
        latitude: lat,
        longitude: lng,
        description: null,
        area_polygon: null,
        start_date: null,
        end_date: null,
        metadata: poiType === 'stage' ? { artists: [] } : null
      };

      createPOIMutation.mutate(newPOI);
    }
  }, [createPOIMutation, updatePOIMutation, setUnplacedPOIs]);

  if (isLoading) {
    return <div className="loading">Loading festival editor...</div>;
  }

  const festival = festivalData?.festival;
  const pois = poisData?.pois || [];



  if (!festival) {
    return <div className="loading">Festival not found</div>;
  }

  // Floating card style (matching UserDashboard)
  const floatingCardStyle = {
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'url("/DesktopLandingPageBG.jpg") center/cover no-repeat fixed',
      position: 'relative'
    }}>
      {/* Background overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(255, 255, 255, 0.3)',
        zIndex: 0
      }} />

      {/* Header - Floating Card */}
      <header style={{
        ...floatingCardStyle,
        margin: '15px 15px 0 15px',
        padding: '0 20px',
        height: '60px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 10,
        position: 'relative'
      }}>
        <h1 style={{ fontSize: '24px', color: '#333' }}>Festivaleer</h1>
        <UserLoginStatusInfolet />
      </header>

      {/* Main Layout */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        padding: '15px',
        gap: '15px',
        zIndex: 1,
        minHeight: 0
      }}>
        {/* Left Sidebar - Floating Card */}
        <nav style={{
          ...floatingCardStyle,
          width: '250px',
          padding: '15px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Festival Name with ellipsis */}
          <h3
            title={festival.name}
            style={{
              margin: '0 0 15px 0',
              fontSize: '14px',
              color: '#333',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {festival.name}
          </h3>

          {/* Basic Information Node */}
          <div style={{ marginBottom: '5px' }}>
            <div
              onClick={() => toggleNode('basic_info')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                color: activeView === 'basic_info' ? '#007bff' : '#666',
                backgroundColor: activeView === 'basic_info' ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              {expandedNodes.basic_info ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Info size={16} />
              <span style={{ fontSize: '14px' }}>Basic Information</span>
            </div>
          </div>

          {/* Map Overview Node */}
          <div style={{ marginBottom: '5px' }}>
            <div
              onClick={() => {
                toggleNode('map_overview');
                setActiveView('map_overview');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                color: activeView === 'map_overview' ? '#007bff' : '#666',
                backgroundColor: activeView === 'map_overview' ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              {expandedNodes.map_overview ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Map size={16} />
              <span style={{ fontSize: '14px' }}>Map Overview</span>
            </div>

            {/* POI List when expanded */}
            {expandedNodes.map_overview && (
              <div style={{ marginLeft: '24px', marginTop: '5px' }}>
                <POIList
                  pois={[...pois, ...unplacedPOIs]}
                  onPOISelect={setSelectedPOI}
                  selectedPOI={selectedPOI}
                  onPOIDrop={handlePOIDrop}
                />
              </div>
            )}
          </div>

          {/* Collaborators Node */}
          <div style={{ marginBottom: '5px' }}>
            <div
              onClick={() => toggleNode('collaborators')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                color: activeView === 'collaborators' ? '#007bff' : '#666',
                backgroundColor: activeView === 'collaborators' ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              {expandedNodes.collaborators ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Users size={16} />
              <span style={{ fontSize: '14px' }}>Collaborators</span>
            </div>
          </div>
        </nav>

        {/* Main Content Area - Floating Card */}
        <div style={{ flex: 1, display: 'flex', gap: '15px' }}>
          {activeView === 'map_overview' && (
            <>
              {/* Center Map - Floating Card */}
              <div style={{ ...floatingCardStyle, flex: 1, position: 'relative', overflow: 'hidden' }}>
                {festival.location_lat != null && festival.location_lng != null ? (
                  <div style={{ height: '100%', width: '100%' }}>
                    <GoogleMap
                      initialLat={festival.location_lat}
                      initialLng={festival.location_lng}
                      initialZoom={festival.zoom_level || 15}
                      height="100%"
                      onLocationSelect={() => {}} // Read-only for location
                      pois={pois}
                      onPOIDrop={handlePOIDrop}
                      onPOISelect={setSelectedPOI}
                      selectedPOI={selectedPOI}
                      showSearch={false} // Hide search in editor
                    />
                  </div>
                ) : (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#666',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>No Location Set</h3>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      Please set a location for this festival in the Basic Information section
                      <br />
                      to enable the map view and POI management.
                    </p>

                  </div>
                )}
              </div>

              {/* Right Panel - Floating Card */}
              <div style={{
                ...floatingCardStyle,
                width: '300px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                {/* Upper Half - POI Inspector */}
                <div style={{
                  height: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <div style={{
                    padding: '15px',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                      POI Inspector
                    </h3>
                  </div>

                  <div style={{ flex: 1, overflow: 'auto', padding: '15px' }}>
                    {selectedPOI ? (
                      <POIInspector
                        poi={selectedPOI}
                        onUpdate={updatePOIMutation.mutate}
                        onDelete={() => deletePOIMutation.mutate(selectedPOI.id)}
                        onCancel={() => setSelectedPOI(null)}
                      />
                    ) : (
                      <div style={{
                        color: '#666',
                        textAlign: 'center',
                        padding: '20px',
                        fontSize: '14px'
                      }}>
                        Select a POI to view details
                      </div>
                    )}
                  </div>
                </div>

                {/* Lower Half - Tab System */}
                <div style={{
                  height: '50%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Tab Headers */}
                  <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <button
                      onClick={() => setActiveTab('quick_create')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        background: activeTab === 'quick_create' ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                        borderBottom: activeTab === 'quick_create' ? '2px solid #007bff' : '2px solid transparent',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: activeTab === 'quick_create' ? 'bold' : 'normal',
                        color: activeTab === 'quick_create' ? '#007bff' : '#666'
                      }}
                    >
                      Quick create
                    </button>
                    <button
                      onClick={() => setActiveTab('bulk_create')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        background: activeTab === 'bulk_create' ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                        borderBottom: activeTab === 'bulk_create' ? '2px solid #007bff' : '2px solid transparent',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: activeTab === 'bulk_create' ? 'bold' : 'normal',
                        color: activeTab === 'bulk_create' ? '#007bff' : '#666'
                      }}
                    >
                      Bulk create
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '15px' }}>
                    {activeTab === 'quick_create' ? (
                      <POIPalette />
                    ) : (
                      <BulkCreateTab
                        onPOIsCreated={(newPOIs) => setUnplacedPOIs(prev => [...prev, ...newPOIs])}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeView === 'basic_info' && (
            <div style={{ ...floatingCardStyle, flex: 1, padding: '20px', overflow: 'auto' }}>
              <FestivalForm
                initialData={{
                  name: festival?.name || '',
                  description: festival?.description || '',
                  start_date: festival?.start_date ? new Date(festival.start_date).toISOString().slice(0, 16) : '',
                  end_date: festival?.end_date ? new Date(festival.end_date).toISOString().slice(0, 16) : '',
                  location_name: festival?.location_name || '',
                  location_lat: festival?.location_lat,
                  location_lng: festival?.location_lng,
                  zoom_level: festival?.zoom_level || 15,
                  status: festival?.status || 'draft'
                }}
                onSubmit={(updatedData) => {
                  updateFestivalMutation.mutate(updatedData);
                }}
                onCancel={() => {}}
                isLoading={updateFestivalMutation.isLoading}
                submitLabel="Save Changes"
                showStatus={true}
                readOnly={true}
                showEditButton={true}
              />
            </div>
          )}

          {activeView === 'collaborators' && (
            <div style={{ ...floatingCardStyle, flex: 1, padding: '20px' }}>
              <CollaboratorsList festivalId={festivalId} />
            </div>
          )}
        </div>
      </div>

      {/* Email Verification Required Dialog */}
      {showVerificationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '450px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <AlertCircle size={60} style={{ color: '#f59e0b', marginBottom: '20px' }} />
            <h2 style={{ marginBottom: '15px', fontSize: '20px' }}>Email Verification Required</h2>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Please verify your email address before publishing festivals.
              You can request a new verification email from your Account page.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => navigate('/user/account')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <Mail size={16} />
                Go to Account
              </button>
              <button
                onClick={() => setShowVerificationDialog(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// POI Palette Component
const POIPalette = () => {
  const handleDragStart = (e, poiType) => {
    e.dataTransfer.setData('text/plain', poiType);
  };

  return (
    <div>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
        Drag POI types onto the map to create new points of interest.
      </p>

      <div style={{ display: 'grid', gap: '8px' }}>
        {POI_TYPES.map(poiType => (
          <div
            key={poiType.id}
            draggable
            onDragStart={(e) => handleDragStart(e, poiType.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              cursor: 'grab',
              background: 'white',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f8f9fa';
              e.target.style.borderColor = '#007bff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.borderColor = '#e0e0e0';
            }}
          >
            <span style={{ fontSize: '20px' }}>{poiType.icon}</span>
            <span style={{ fontSize: '14px' }}>{poiType.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// POI Inspector Component
const POIInspector = ({ poi, onUpdate, onDelete, onCancel }) => {
  const [editedPOI, setEditedPOI] = useState({ ...poi });
  const [artists, setArtists] = useState(poi.metadata?.artists || []);

  // Update state when poi prop changes
  useEffect(() => {
    setEditedPOI({ ...poi });
    setArtists(poi.metadata?.artists || []);
  }, [poi]);

  const handleSave = () => {
    const updatedPOI = {
      ...editedPOI,
      metadata: poi.category === 'stage' ? { artists } : editedPOI.metadata
    };
    onUpdate({ id: poi.id, ...updatedPOI });
  };

  const addArtist = () => {
    setArtists([...artists, { name: '', start_time: '', end_time: '' }]);
  };

  const updateArtist = (index, field, value) => {
    const newArtists = [...artists];
    newArtists[index][field] = value;
    setArtists(newArtists);
  };

  const removeArtist = (index) => {
    setArtists(artists.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
          <span style={{ fontSize: '24px' }}>
            {POI_ICONS[poi.category] || POI_ICONS.other}
          </span>
          <div>
            <h4 style={{ margin: 0, fontSize: '16px' }}>
              {POI_TYPES.find(t => t.id === poi.category)?.name || 'Unknown'}
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              ID: {poi.id}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
            Name
          </label>
          <input
            type="text"
            value={editedPOI.name || ''}
            onChange={(e) => setEditedPOI({ ...editedPOI, name: e.target.value })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
            Description
          </label>
          <textarea
            value={editedPOI.description || ''}
            onChange={(e) => setEditedPOI({ ...editedPOI, description: e.target.value })}
            rows="3"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Stage-specific artist management */}
        {poi.category === 'stage' && (
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500' }}>
                Artists
              </label>
              <button
                onClick={addArtist}
                style={{
                  padding: '4px 8px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                + Add Artist
              </button>
            </div>

            <div style={{ display: 'grid', gap: '8px' }}>
              {artists.map((artist, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 60px 60px 30px',
                  gap: '5px',
                  alignItems: 'center',
                  padding: '8px',
                  background: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  <input
                    type="text"
                    placeholder="Artist name"
                    value={artist.name}
                    onChange={(e) => updateArtist(index, 'name', e.target.value)}
                    style={{
                      padding: '4px',
                      border: '1px solid #ddd',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <input
                    type="time"
                    value={artist.start_time}
                    onChange={(e) => updateArtist(index, 'start_time', e.target.value)}
                    style={{
                      padding: '4px',
                      border: '1px solid #ddd',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <input
                    type="time"
                    value={artist.end_time}
                    onChange={(e) => updateArtist(index, 'end_time', e.target.value)}
                    style={{
                      padding: '4px',
                      border: '1px solid #ddd',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <button
                    onClick={() => removeArtist(index)}
                    style={{
                      padding: '4px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '8px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px'
          }}
        >
          <Save size={14} />
          Save
        </button>
        <button
          onClick={onDelete}
          style={{
            padding: '8px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <Trash2 size={14} />
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '8px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

// Bulk Create Tab Component
const BulkCreateTab = ({ onPOIsCreated }) => {
  const [bulkText, setBulkText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [parsedPOIs, setParsedPOIs] = useState([]);
  const [parseError, setParseError] = useState('');

  const parseBulkText = (text) => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    const pois = [];
    const errors = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Simple parsing: "Type: Name" or "Type: Name - Description"
      const match = trimmedLine.match(/^\s*?(.+?):\s*(.*?)(?:\s+-\s+(.*?))?\s*$/);
      

      if (match) {
        const [, type, name, description] = match;
        const poiType = POI_TYPES.find(t =>
          t.id.toLowerCase() === type.toLowerCase() ||
          t.name.toLowerCase() === type.toLowerCase()
        );

        if (poiType) {
          pois.push({
            id: `temp_${Date.now()}_${index}`,
            name: name.trim(),
            category: poiType.id,
            description: description ? description.trim() : null,
            latitude: null,
            longitude: null,
            isUnplaced: true
          });
        } else {
          errors.push(`Line ${index + 1}: Unknown POI type "${type}"`);
        }
      } else {
        errors.push(`Line ${index + 1}: Invalid format. Use "Type: Name" or "Type: Name - Description"`);
      }
    });

    return { pois, errors };
  };

  const handlePreview = () => {
    if (!bulkText.trim()) {
      setParseError('Please enter some text to parse');
      return;
    }

    const { pois, errors } = parseBulkText(bulkText);

    if (errors.length > 0) {
      setParseError(errors.join('\n'));
      return;
    }

    if (pois.length === 0) {
      setParseError('No valid POIs found in the text');
      return;
    }

    setParsedPOIs(pois);
    setParseError('');
    setShowPreview(true);
  };

  const handleCreate = () => {
    onPOIsCreated(parsedPOIs);
    setBulkText('');
    setParsedPOIs([]);
    setShowPreview(false);
    setParseError('');
  };

  const handleCancel = () => {
    setShowPreview(false);
    setParseError('');
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!showPreview ? (
        <>
          <div style={{ marginBottom: '10px' }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
              Enter POIs in the format: <strong>Type: Name</strong> or <strong>Type: Name - Description</strong>
            </p>
            <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#888' }}>
              Available types: {POI_TYPES.map(t => t.name).join(', ')}
            </p>
          </div>

          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder="Example:&#10;Food: Pizza Stand - Delicious pizza&#10;Restroom: Main Restrooms&#10;Stage: Main Stage - Primary performance area"
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'monospace',
              resize: 'none',
              minHeight: '150px'
            }}
          />

          {parseError && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24',
              fontSize: '12px',
              whiteSpace: 'pre-line'
            }}>
              {parseError}
            </div>
          )}

          <button
            onClick={handlePreview}
            disabled={!bulkText.trim()}
            style={{
              marginTop: '15px',
              padding: '10px 20px',
              background: bulkText.trim() ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: bulkText.trim() ? 'pointer' : 'not-allowed',
              fontSize: '14px'
            }}
          >
            Preview
          </button>
        </>
      ) : (
        <>
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
              Preview ({parsedPOIs.length} POIs)
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              These POIs will be created without map placement
            </p>
          </div>

          <div style={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '10px'
          }}>
            {parsedPOIs.map((poi, index) => (
              <div key={poi.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px',
                borderBottom: index < parsedPOIs.length - 1 ? '1px solid #eee' : 'none'
              }}>
                <span style={{ fontSize: '16px' }}>
                  {POI_TYPES.find(t => t.id === poi.category)?.icon || '📍'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {poi.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {POI_TYPES.find(t => t.id === poi.category)?.name}
                    {poi.description && ` • ${poi.description}`}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex',
            gap: '10px',
            marginTop: '15px'
          }}>
            <button
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '10px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              style={{
                flex: 1,
                padding: '10px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Create
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// POI List Component for Map Overview
const POIList = ({ pois, onPOISelect, selectedPOI, onPOIDrop }) => {
  // Group POIs by category and sort
  const groupedPOIs = pois.reduce((groups, poi) => {
    const category = poi.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(poi);
    return groups;
  }, {});

  // Sort categories and POIs within each category
  const sortedCategories = Object.keys(groupedPOIs).sort();
  sortedCategories.forEach(category => {
    groupedPOIs[category].sort((a, b) => a.name.localeCompare(b.name));
  });

  const handlePOIClick = (poi) => {
    onPOISelect(poi);
  };

  const handleDragStart = (e, poi) => {
    if (poi.isUnplaced) {
      e.dataTransfer.setData('text/plain', JSON.stringify({
        type: 'unplaced_poi',
        poi: poi
      }));
    }
  };

  return (
    <div style={{ fontSize: '12px' }}>
      {sortedCategories.map(category => {
        const categoryPOIs = groupedPOIs[category];
        const categoryType = POI_TYPES.find(t => t.id === category);

        return (
          <div key={category} style={{ marginBottom: '8px' }}>
            {/* Category Header */}
            <div style={{
              color: '#555',
              fontWeight: 'bold',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>{categoryType?.icon || '📍'}</span>
              <span>{categoryType?.name || category}</span>
              <span>({categoryPOIs.length})</span>
            </div>

            {/* POIs in Category */}
            {categoryPOIs.map(poi => (
              <div
                key={poi.id}
                draggable={poi.isUnplaced}
                onDragStart={(e) => handleDragStart(e, poi)}
                onClick={() => handlePOIClick(poi)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  marginBottom: '2px',
                  cursor: poi.isUnplaced ? 'grab' : 'pointer',
                  borderRadius: '3px',
                  background: selectedPOI?.id === poi.id ? 'rgba(0, 123, 255, 0.2)' : 'transparent',
                  color: selectedPOI?.id === poi.id ? '#007bff' : '#666',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (selectedPOI?.id !== poi.id) {
                    e.target.style.background = 'rgba(0, 123, 255, 0.1)';
                    e.target.style.color = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPOI?.id !== poi.id) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#666';
                  }
                }}
              >
                <span style={{ fontSize: '10px', opacity: 0.8 }}>
                  {categoryType?.icon || '📍'}
                </span>
                <span style={{ flex: 1, fontSize: '11px' }}>
                  {poi.name}
                </span>
                {poi.isUnplaced && (
                  <span style={{
                    color: '#e74c3c',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    ❓
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {pois.length === 0 && (
        <div style={{
          color: '#999',
          fontStyle: 'italic',
          textAlign: 'center',
          padding: '10px'
        }}>
          No POIs created yet
        </div>
      )}
    </div>
  );
};

export default FestivalEditor;
